/**
 * Coordinate Synchronization Service — viewer position EVENT BUS only.
 *
 * Scope (deliberate): register/unregister viewer callbacks and broadcast position
 * updates between viewer modes (3D / 2D / Panorama). Nothing else.
 *
 * Coordinate MATH lives elsewhere by design — do not add transforms here:
 *  - CoordinateTransformService: planar similarity transforms (model <-> map)
 *  - src/utils/CoordinateTransformer: geodetic conversions (proj4, EPSG:2039/4326)
 *
 * (2026-07-03 refactor DUP-05: ~14 unused transform/geo/distance methods were removed;
 * they duplicated the two services above and had zero call sites.)
 */

import { WALK_EYE_HEIGHT_METERS } from './spatial/EyeHeights';

export interface ViewPosition {
  x: number;
  y: number;
  z: number;
  heading: number; // rotation around Y-axis in radians
  pitch: number;   // rotation around X-axis in radians
  roll?: number;   // rotation around Z-axis in radians (optional)
}

export interface ViewerMode {
  type: '3D' | '2D' | 'Panorama';
  id: string;
}

export interface SyncEvent {
  sourceViewer: ViewerMode;
  position: ViewPosition;
  timestamp: number;
}

export type SyncCallback = (event: SyncEvent) => void;

class CoordinateSyncService {
  private callbacks: Map<string, SyncCallback[]> = new Map();
  private currentPosition: ViewPosition = {
    x: 0,
    y: WALK_EYE_HEIGHT_METERS, // Default human eye level
    z: 0,
    heading: 0,
    pitch: 0,
    roll: 0
  };

  /**
   * Register a viewer for coordinate synchronization.
   * The callback is immediately invoked with the current position.
   */
  registerViewer(viewerId: string, callback: SyncCallback): void {
    if (!this.callbacks.has(viewerId)) {
      this.callbacks.set(viewerId, []);
    }
    this.callbacks.get(viewerId)!.push(callback);

    // Send current position to newly registered viewer
    const event: SyncEvent = {
      sourceViewer: { type: '3D', id: 'system' },
      position: { ...this.currentPosition },
      timestamp: Date.now()
    };
    callback(event);
  }

  /**
   * Unregister a viewer from coordinate synchronization
   */
  unregisterViewer(viewerId: string): void {
    this.callbacks.delete(viewerId);
  }

  /**
   * Update position from a specific viewer and broadcast to all OTHER viewers.
   */
  updatePosition(sourceViewer: ViewerMode, position: ViewPosition): void {
    this.currentPosition = { ...position };

    const event: SyncEvent = {
      sourceViewer,
      position: { ...position },
      timestamp: Date.now()
    };

    for (const [viewerId, callbacks] of this.callbacks.entries()) {
      if (viewerId !== sourceViewer.id) {
        callbacks.forEach(callback => {
          try {
            callback(event);
          } catch (error) {
            console.error(`Error in sync callback for viewer ${viewerId}:`, error);
          }
        });
      }
    }
  }

  /**
   * Get current synchronized position
   */
  getCurrentPosition(): ViewPosition {
    return { ...this.currentPosition };
  }
}

// Singleton instance
export const coordinateSync = new CoordinateSyncService();
export default coordinateSync;
